import React, { Fragment, useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

const MAX_IMAGES = 5;

const reviewSchema = Yup.object().shape({
  title: Yup.string().required("Required"),
  name: Yup.string().required("Required"),
  rating: Yup.number().required("Required"),
  message: Yup.string().required("Required"),
  // email: Yup.string().email().required("Required"),
});

const buildFormData = (data, objectName, fileKey) => {

  const formData = new FormData();
  Object.keys(data).map((vKey) => {
    if (vKey != fileKey) {
      formData.append(`${objectName}[${vKey}]`, data[vKey]);
    } else if (data[vKey]) {
      [...data[vKey]].map((file, idx) => {
        formData.append(`${objectName}[${vKey}][]`, file);
      })
    }
  });
  return formData;
}
const ReviewForm = (props) => {
  const { listing, currentUser, loginPath, existingReview } = props;
  const [isLoading, setIsLoading] = useState(false)
  const [serverErrors, setServerErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [imagesError, setImagesError] = useState(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const formRef = useRef();
  const isEditing = !!existingReview?.id;
  const stars = [
    { label: 1, value: 1 },
    { label: 2, value: 2 },
    { label: 3, value: 3 },
    { label: 4, value: 4 },
    { label: 5, value: 5 },
  ];

  const handleSave = (data, { resetForm }) => {
    if (!currentUser?.id) {
      setNeedsLogin(true);
      return;
    }
    setIsLoading(true);
    setServerErrors([]);
    setSuccessMessage(null);
    const url = isEditing
      ? `/listing/${listing.slug}/reviews/${existingReview.id}`
      : `/listing/${listing.slug}/reviews`;
    fetch(url, {
      method: isEditing ? 'PUT' : 'POST',
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          ?.content,
      },
      body: buildFormData(data, 'review', 'images')
    }).then((resp) => {
      return resp.json().then((json) => ({ ok: resp.ok, json }));
    }).then(({ ok, json }) => {
      setIsLoading(false);
      if (!ok) {
        setServerErrors(json.errors || ["Something went wrong. Please try again."]);
        return;
      }
      setSuccessMessage(
        isEditing
          ? "Your review has been updated successfully."
          : "Your review has been submitted successfully."
      );
      if (props.onSubmitted) {
        props.onSubmitted(json);
      }
      if (!isEditing) {
        resetForm();
        formRef.current.reset();
      }
    }).catch((error) => {
      setIsLoading(false);
      setServerErrors(["Something went wrong. Please try again."]);
    })
  }

  const formik = useFormik({
    initialValues: {
      name: currentUser?.display_name || ''
    },
    validationSchema: reviewSchema, // replace with your actual validation schema
    onSubmit: handleSave, // replace with your form submission function
  });

  const { values, handleSubmit, handleChange, handleBlur, errors, touched, setFieldValue, setValues } =
    formik;

  useEffect(() => {
    if (existingReview) {
      setValues({
        title: existingReview.title || '',
        name: existingReview.name || currentUser?.display_name || '',
        message: existingReview.message || '',
        rating: existingReview.rating || undefined,
      });
    }
  }, [existingReview?.id]);

  const handleImagesChange = (e) => {
    const files = e.target.files;
    if (files && files.length > MAX_IMAGES) {
      setImagesError(`You can add up to ${MAX_IMAGES} photos only.`);
      setFieldValue('images', null);
      e.target.value = '';
      return;
    }
    setImagesError(null);
    setFieldValue('images', files);
  }

  return (
    <Fragment>
      <form onSubmit={handleSubmit} ref={formRef}>
        <div id="utf_add_review" className="utf_add_review-box">
          <h3 className="utf_listing_headline_part margin-bottom-20">
            {isEditing ? 'Edit your review' : 'Submit your review'}
          </h3>
          <span className="utf_leave_rating_title">
            Your email address will not be published. Required fields are marked{" "}
            <sup>*</sup>
          </span>
          {isEditing && (
            <div style={{ fontSize: '13px', color: 'var(--white40)', marginTop: '8px' }}>
              You've already reviewed this listing. Saving below will update your existing review.
            </div>
          )}
          {!currentUser?.id && (
            <div style={{ fontSize: '13px', color: 'var(--white40)', marginTop: '8px' }}>
              You'll need to <a style={{textDecoration: 'underline', fontWeight: 'bold'}} href={loginPath || '/users/sign_in'}>log in</a> before your review can be published.
            </div>
          )}
          {needsLogin && (
            <div style={{ color: '#d9534f', fontSize: '13px', marginTop: '8px' }}>
              Please <a href={loginPath || '/users/sign_in'}>log in</a> to submit your review.
            </div>
          )}
          <div className="row">
            <div className="col-md-6 col-sm-6 col-xs-12">
              <div className="utf_leave_rating margin-bottom-30" style={{ position: 'relative' }}>
                <input
                  type="text"
                  name="rating_confirmation"
                  value={values.rating || ''}
                  onChange={() => {}}
                  required
                  tabIndex={-1}
                  style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: 0,
                    border: 'none',
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                />
                {
                  [5, 4, 3, 2, 1].map((num) => (
                    <Fragment key={num}>
                      <input
                        type="radio"
                        name="rating"
                        id={`rating-${num}`}
                        value={num}
                        checked={values.rating === num}
                        onChange={() => setFieldValue('rating', num)}
                      />
                      <label htmlFor={`rating-${num}`} aria-label={`${num} star${num > 1 ? 's' : ''}`}>★</label>
                    </Fragment>
                  ))
                }
              </div>
            </div>
            <div className="col-md-6 col-sm-6 col-xs-12" style={{ textAlign: 'right' }}>
              <div className="add-review-photos margin-bottom-30">
                <div className="photoUpload">
                  <span>
                    Add Photos (max {MAX_IMAGES}) <i className="sl sl-icon-arrow-up-circle"></i>
                  </span>
                  <input
                    type="file"
                    name="images"
                    className="upload"
                    multiple
                    accept="image/*"
                    onChange={handleImagesChange}
                  />
                </div>
                {values.images && values.images.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--white40)', marginTop: '4px' }}>
                    {values.images.length} files selected
                  </div>
                )}
                {imagesError && (
                  <div style={{ fontSize: '12px', color: '#d9534f', marginTop: '4px' }}>
                    {imagesError}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div id="utf_add_comment" className="utf_add_comment">
              <div className="row">
                <div className="col-md-12">
                  <input
                    type="text"
                    name="title"
                    id="title"
                    placeholder="Title of the review*"
                    autoComplete="review-title"
                    value={`${values.title || ""}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required={true}
                    className={errors.title && touched.title ? " error" : ""}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    placeholder="Your display name*"
                    autoComplete="name"
                    value={`${values.name || ""}`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required={true}
                    className={errors.name && touched.name ? " error" : ""}
                  />
                </div>
              </div>
              <div>
                <textarea
                  type="text"
                  name="message"
                  id="message"
                  placeholder="Your review*"
                  autoComplete="off"
                  value={`${values.message || ""}`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required={true}
                  className={errors.message && touched.message ? " error" : ""}
                  rows="3"
                  cols="40"
                />
              </div>
            <button className="button" type="submit" disabled={isLoading}>
              {isEditing ? 'Update Review' : 'Submit Review'}
            </button>
            <div className="clearfix"></div>
                      {serverErrors.length > 0 && (
            <div style={{ color: '#d9534f', fontSize: '13px', marginTop: '8px' }}>
              {serverErrors.map((err, idx) => <div key={idx}>{err}</div>)}
            </div>
          )}
          {successMessage && (
            <div style={{ color: '#5cb85c', fontSize: '13px', marginTop: '8px' }}>
              {successMessage}
            </div>
          )}

          </div>
        </div>
      </form>
    </Fragment>
  );
};
export default ReviewForm;
